import { createContext, useContext, type PropsWithChildren } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { Session } from "../types";

type AuthContextValue = {
  session: Session | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<Session>;
  acceptInvite: (payload: { token: string; username: string; password: string; consents: string[] }) => Promise<Session>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const sessionQuery = useQuery({
    queryKey: ["session"],
    queryFn: api.session,
    retry: false,
    staleTime: 60_000,
  });
  const loginMutation = useMutation({ mutationFn: ({ username, password }: { username: string; password: string }) => api.login(username, password) });
  const inviteMutation = useMutation({ mutationFn: api.acceptInvite });
  const logoutMutation = useMutation({ mutationFn: api.logout });

  return (
    <AuthContext.Provider value={{
      session: sessionQuery.data ?? null,
      loading: sessionQuery.isLoading,
      login: async (username, password) => {
        const session = await loginMutation.mutateAsync({ username, password });
        queryClient.setQueryData(["session"], session);
        return session;
      },
      acceptInvite: async (payload) => {
        const session = await inviteMutation.mutateAsync(payload);
        queryClient.setQueryData(["session"], session);
        return session;
      },
      logout: async () => {
        await logoutMutation.mutateAsync();
        queryClient.setQueryData(["session"], null);
      },
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used within AuthProvider");
  return value;
}
